import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import JobCard from '../components/JobCard';
import {
  Star,
  MapPin,
  Calendar,
  Shield,
  Briefcase,
  Loader2,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser, token } = useAuth();

  const [profileUser, setProfileUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // If no userId, show current user's profile
  const targetUserId = userId || currentUser?.user_id;
  const isOwnProfile = targetUserId === currentUser?.user_id;

  useEffect(() => {
    const fetchData = async () => {
      if (!targetUserId) return;

      try {
        // Fetch user profile
        const userResponse = await fetch(`${API_URL}/api/users/${targetUserId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });

        if (!userResponse.ok) throw new Error('User not found');
        const userData = await userResponse.json();
        setProfileUser(userData);

        // Fetch reviews
        const reviewsResponse = await fetch(`${API_URL}/api/users/${targetUserId}/reviews`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });

        if (reviewsResponse.ok) {
          setReviews(await reviewsResponse.json());
        }

        // Fetch user's jobs (as employer or worker)
        const jobsResponse = await fetch(`${API_URL}/api/users/${targetUserId}/jobs?role=${userData.role}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });

        if (jobsResponse.ok) {
          setJobs(await jobsResponse.json());
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [targetUserId, token, t]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="text-center py-20">
          <p className="text-slate-500">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Back button (if viewing other user) */}
        {!isOwnProfile && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.back')}</span>
          </button>
        )}

        {/* Profile Header */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="h-32 bg-gradient-to-r from-slate-800 to-slate-900"></div>
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
              <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                <AvatarImage src={profileUser.photo_url} />
                <AvatarFallback className="bg-slate-200 text-2xl">
                  {profileUser.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 pt-2 sm:pt-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                      {profileUser.name}
                      {profileUser.verified && (
                        <Shield className="w-5 h-5 text-emerald-500" />
                      )}
                    </h1>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                      <Badge variant="secondary" className="capitalize">
                        {profileUser.role}
                      </Badge>
                      {profileUser.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="font-semibold text-slate-700">{profileUser.rating.toFixed(1)}</span>
                          <span>({profileUser.review_count})</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {isOwnProfile ? (
                    <Button
                      data-testid="edit-profile-btn"
                      onClick={() => navigate('/settings')}
                      variant="outline"
                    >
                      {t('profile.edit')}
                    </Button>
                  ) : (
                    <Button
                      data-testid="contact-user-btn"
                      onClick={() => toast.info('Start from a job to contact this user')}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t('profile.contact')}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{profileUser.review_count || 0}</p>
                <p className="text-sm text-slate-500">{t('profile.reviews')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{jobs.length}</p>
                <p className="text-sm text-slate-500">{t('jobs.my_jobs')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">
                  {profileUser.rating > 0 ? profileUser.rating.toFixed(1) : '-'}
                </p>
                <p className="text-sm text-slate-500">{t('profile.rating')}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-900">{formatDate(profileUser.created_at)}</p>
                <p className="text-sm text-slate-500">{t('profile.member_since')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="jobs" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="jobs" className="data-[state=active]:bg-slate-100">
              <Briefcase className="w-4 h-4 mr-2" />
              {t('jobs.my_jobs')} ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-slate-100">
              <Star className="w-4 h-4 mr-2" />
              {t('profile.reviews')} ({reviews.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="space-y-3">
            {jobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('jobs.no_jobs')}</p>
              </div>
            ) : (
              jobs.map((job, index) => (
                <JobCard
                  key={job.job_id}
                  job={job}
                  index={index}
                  onClick={() => navigate(`/jobs/${job.job_id}`)}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4">
            {reviews.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('profile.no_reviews')}</p>
              </div>
            ) : (
              reviews.map((review) => (
                <div
                  key={review.review_id}
                  className="bg-white rounded-xl border border-slate-200 p-4"
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-slate-200">
                        {review.from_user_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-slate-900">{review.from_user_name}</p>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < review.rating
                                  ? 'text-amber-500 fill-amber-500'
                                  : 'text-slate-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-600 mt-2">{review.comment}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {formatDate(review.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
